import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { UserPlus, Send, CheckCircle2, Trash2, XCircle, Pencil, ArrowDownUp, UserCheck, IndianRupee, FileText, History as HistoryIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { t, getLang } from "@/lib/i18n";

const ICON = {
  added: [UserPlus, "#2E7D32"], submitted: [Send, "#C59B27"], approved: [CheckCircle2, "#2E7D32"], removed: [Trash2, "#C62828"],
  rejected: [XCircle, "#C62828"], edited: [Pencil, "#1E3A2B"], reordered: [ArrowDownUp, "#D96B43"], networth: [IndianRupee, "#C59B27"], document: [FileText, "#C62828"], claimed: [UserCheck, "#1E3A2B"],
};
const VERB = {
  added: "added", submitted: "submitted", approved: "approved", removed: "removed", rejected: "rejected",
  edited: "edited", reordered: "reordered siblings", networth: "updated net worth of", document: "added a document for", claimed: "claimed",
};
const FILTERS = ["all", "added", "submitted", "approved", "removed", "reordered", "edited", "claimed", "networth"];
const PAGE = 50;

const Row = ({ h }) => {
  const [Icon, color] = ICON[h.action] || [HistoryIcon, "#7E8E85"];
  const when = new Date(h.created_at).toLocaleString(getLang() === "hi" ? "hi-IN" : undefined, { dateStyle: "medium", timeStyle: "short" });
  const linkable = h.person_id && !["removed", "rejected"].includes(h.action);
  return (
    <li className="flex gap-4 py-4" data-testid={`history-row-${h.history_id}`}>
      <span className="w-10 h-10 shrink-0 rounded-full grid place-items-center" style={{ background: `${color}1A` }}><Icon className="w-4 h-4" style={{ color }} /></span>
      <div className="flex-1 min-w-0">
        <p className="text-[15px]">
          <b>{h.actor_name}</b> {t(VERB[h.action] || h.action)}{" "}
          {h.person_name && (linkable ? <Link to={`/person/${h.person_id}`} className="font-semibold text-[#1E3A2B] hover:text-[#D96B43]">{h.person_name}</Link> : <b>{h.person_name}</b>)}
        </p>
        {h.detail && <p className="text-sm text-[#52635B] mt-0.5">{h.detail}</p>}
        <p className="font-mono text-[11px] text-[#7E8E85] mt-1">{when}</p>
      </div>
    </li>
  );
};

export default function History() {
  const { user, loading } = useAuth();
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState("all");
  const load = useCallback((skip = 0) => api.get("/history", { params: { skip, limit: PAGE } }).then(({ data }) => {
    setItems((prev) => (skip ? [...(prev || []), ...data.items] : data.items));
    setTotal(data.total);
  }), []);
  useEffect(() => { if (user?.can_vote) load(); }, [user, load]);
  if (loading) return null;
  if (!user?.can_vote) return <Navigate to="/" replace />;
  const shown = (items || []).filter((h) => filter === "all" || h.action === filter);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-14 space-y-8">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#D96B43] mb-3 flex items-center gap-1.5"><HistoryIcon className="w-3.5 h-3.5" />{t("Change history")}</p>
        <h1 className="font-serif text-4xl sm:text-5xl font-bold tracking-tight">{t("Who changed what, and when")}</h1>
        <p className="mt-3 text-[#52635B]">{t("Every addition, approval, removal and re-ordering on the family tree.")}</p>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="history-filters">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`history-filter-${f}`}
            className={`px-4 h-9 rounded-full text-sm border transition-colors ${filter === f ? "bg-[#1E3A2B] text-white border-transparent" : "bg-white border-[#E5DEC9] hover:border-[#1E3A2B]"}`}>
            {t(f === "all" ? "All" : f === "reordered" ? "reordered siblings" : f === "networth" ? "Net worth" : f)}
          </button>
        ))}
      </div>
      {items === null ? <div className="h-48 rounded-3xl bg-[#F4EDE2] animate-pulse" /> : shown.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-[#CDBF9F] p-14 text-center text-[#7E8E85]" data-testid="history-empty">{t("No changes recorded yet.")}</div>
      ) : (
        <ul className="divide-y divide-[#E5DEC9] rounded-3xl border border-[#E5DEC9] bg-white px-6" data-testid="history-list">
          {shown.map((h) => <Row key={h.history_id} h={h} />)}
        </ul>
      )}
      {items && items.length < total && (
        <Button variant="outline" className="rounded-full" onClick={() => load(items.length)} data-testid="history-load-more">{t("Load more")}</Button>
      )}
    </div>
  );
}
